import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  useTheme,
} from '@mui/material';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useTranslation } from 'react-i18next';

import api from '../api/axiosConfig';
import ClipCard from '../screens/Instat/ClipCard';
import TelestrationEditor from '../screens/Instat/Telestration/TelestrationEditor';

const SEARCH_THRESHOLD = 5;
const NO_TAG_KEY = '__no_tag__';

// Lazy-loaded section rendered inside each informe card. Fetches clips
// linked to the informe (via /api/instat/clips?informeId=X) only when the
// parent card is expanded. Renders the clips in tabs — one per tag, plus a
// "Sans tag" tab when applicable. A clip with N tags appears in N tabs.
//
// Returns null when the informe has no linked clips (no empty card).
export default function InformeClipsSection({ informeId, expanded }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [clips, setClips] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(null);
  const [playingClip, setPlayingClip] = useState(null);
  const fetchedFor = useRef(null);

  const fetchClips = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/instat/clips', { params: { informeId } });
      setClips(data?.clips || []);
      fetchedFor.current = informeId;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[InformeClipsSection] fetch failed:', e?.message);
      setClips([]);
      setError(e?.message || 'error');
    } finally {
      setLoading(false);
    }
  }, [informeId]);

  useEffect(() => {
    if (!expanded) return;
    if (fetchedFor.current === informeId) return;
    fetchClips();
  }, [expanded, informeId, fetchClips]);

  // Tags actually present in this informe's clips (in club tag order).
  const availableTags = useMemo(() => {
    if (!clips) return [];
    const map = new Map();
    clips.forEach((c) => {
      (c.tags || []).forEach((tag) => {
        if (!map.has(tag.id)) map.set(tag.id, tag);
      });
    });
    return Array.from(map.values());
  }, [clips]);

  const hasUntagged = useMemo(
    () => !!(clips && clips.some((c) => !c.tags || c.tags.length === 0)),
    [clips],
  );

  // Apply search filter once; the per-tab split is computed from the result.
  const filteredClips = useMemo(() => {
    if (!clips) return [];
    const q = search.trim().toLowerCase();
    if (!q) return clips;
    return clips.filter((c) => {
      const hay = `${c.match_title || ''} ${c.title || ''} ${c.fname || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [clips, search]);

  // Build the tab list — one per tag with at least one matching clip, plus
  // "Sans tag" if any matching clip is untagged. Tabs vanish when the search
  // empties them so we don't surface dead headers.
  const tabs = useMemo(() => {
    if (!filteredClips.length) return [];
    const byTag = new Map();
    const untagged = [];
    filteredClips.forEach((c) => {
      if (!c.tags || c.tags.length === 0) {
        untagged.push(c);
        return;
      }
      c.tags.forEach((tg) => {
        if (!byTag.has(tg.id)) byTag.set(tg.id, { tag: tg, clips: [] });
        byTag.get(tg.id).clips.push(c);
      });
    });
    const list = availableTags
      .filter((tag) => byTag.has(tag.id))
      .map((tag) => ({
        key: String(tag.id),
        label: tag.libelle,
        tag,
        clips: byTag.get(tag.id).clips,
      }));
    if (untagged.length) {
      list.push({
        key: NO_TAG_KEY,
        label: t('playerCardCreator.informeTab.clips.noTag'),
        tag: null,
        clips: untagged,
      });
    }
    return list;
  }, [filteredClips, availableTags, t]);

  // Keep activeTab valid when tabs change (e.g. search filters one out).
  useEffect(() => {
    if (!tabs.length) {
      if (activeTab !== null) setActiveTab(null);
      return;
    }
    if (!tabs.some((tb) => tb.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
  }, [tabs, activeTab]);

  const handleTagsChange = (clipId, newTags) => {
    setClips((prev) => (prev ? prev.map((c) => (c.id === clipId ? { ...c, tags: newTags } : c)) : prev));
    setPlayingClip((prev) => (prev && prev.id === clipId ? { ...prev, tags: newTags } : prev));
  };

  // No clips at all → render nothing (no empty card on the informe).
  if (clips !== null && clips.length === 0) return null;

  const currentTab = tabs.find((tb) => tb.key === activeTab) || tabs[0];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mt: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <VideocamRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {t('playerCardCreator.informeTab.clips.title')}
            {clips !== null && ` (${clips.length})`}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {clips !== null && clips.length >= SEARCH_THRESHOLD && (
            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('playerCardCreator.informeTab.clips.searchPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 220, '& .MuiInputBase-root': { fontSize: 13 } }}
            />
          )}
          <Tooltip title={t('playerCardCreator.informeTab.clips.refresh')}>
            <span>
              <IconButton size="small" onClick={fetchClips} disabled={loading}>
                {loading
                  ? <CircularProgress size={14} />
                  : <RefreshRoundedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && clips === null && (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t('playerCardCreator.informeTab.clips.loading')}
          </Typography>
        </Stack>
      )}

      {error && !loading && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {clips && clips.length > 0 && tabs.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('playerCardCreator.informeTab.clips.noResults')}
        </Typography>
      )}

      {tabs.length > 0 && (
        <>
          <Tabs
            value={currentTab.key}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              mb: 1.5,
              minHeight: 36,
              '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' },
            }}
          >
            {tabs.map((tb) => (
              <Tab
                key={tb.key}
                value={tb.key}
                label={
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    {tb.tag && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: tb.tag.code_couleur,
                          border: '1px solid rgba(0,0,0,0.08)',
                        }}
                      />
                    )}
                    <Typography
                      component="span"
                      sx={{ fontSize: 13, fontWeight: 600 }}
                    >
                      {tb.label}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ fontSize: 11, color: 'text.secondary' }}
                    >
                      {tb.clips.length}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </Tabs>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(200px, 1fr))',
                sm: 'repeat(auto-fill, minmax(220px, 1fr))',
                md: 'repeat(auto-fill, minmax(240px, 1fr))',
              },
              gap: 1.25,
            }}
          >
            {currentTab.clips.map((clip) => (
              <ClipCard
                key={clip.id}
                clip={clip}
                onPlay={setPlayingClip}
                t={t}
                width="100%"
              />
            ))}
          </Box>
        </>
      )}

      <TelestrationEditor
        clip={playingClip}
        open={!!playingClip}
        onClose={() => setPlayingClip(null)}
        playlist={filteredClips}
        onNavigate={setPlayingClip}
        onTagsChange={handleTagsChange}
      />
    </Paper>
  );
}
